/**
 * Transaction Categorization API Integration
 * Provides auto-categorization of transaction descriptions using AI and keyword matching
 */

import { matchKeywordCategory } from './categoryKeywords.js';
import { API_ENDPOINTS, makeAuthenticatedApiRequest } from './apiConfig.ts';
import { useAuth } from '../composables/useAuth.js';

/**
 * Categorize transaction using AI Lambda function
 * @param {string} description - Transaction description
 * @returns {Promise<Object>} AI categorization response
 */
export async function categorizeWithAI(description) {
  try {
    const { getAccessToken } = useAuth();
    const accessToken = await getAccessToken();
    
    const data = await makeAuthenticatedApiRequest(
      API_ENDPOINTS.TRANSACTION_CATEGORIZATION, 
      { description: description },
      accessToken
    );
    
    return data;
  } catch (error) {
    console.error('Error calling AI Lambda function:', error);
    return null;
  }
}

/**
 * Auto-categorize a transaction using keyword matching and AI
 * Returns enhanced transaction object with category
 * Checks keyword mappings first, then falls back to AI Lambda function
 * @param {Object} transaction - Transaction object with description, amount, date
 * @returns {Promise<Object>} Transaction with added category field
 */
export async function autoCategorizeTransaction(transaction) {
  // Check keyword mappings first
  const keywordCategory = matchKeywordCategory(transaction.description);
  if (keywordCategory) {
    return {
      ...transaction,
      category: keywordCategory,
      categorySource: 'keyword'
    };
  }

  // Fall back to AI Lambda function
  const aiData = await categorizeWithAI(transaction.description);
  if (aiData && aiData.data && aiData.data.category) {
    console.log(aiData.data.category);
    return {
      ...transaction,
      category: aiData.data.category,
      merchantName: aiData.data.merchant || null,
      confidence: aiData.data.confidence || null,
      categorySource: 'ai'
    };
  }
  
  // Return transaction unchanged if no categorization found
  return transaction;
}
